import React, { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext(null);

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// Decoded client-side, no signature check — only ever used to decide what
// the UI shows (e.g. hiding Settings for a non-owner role); the server
// re-checks every permission on every request regardless (CLAUDE.md §3:
// "client-side hiding is cosmetic only").
function decodeRole(t) {
  try { return JSON.parse(atob(t.split(".")[1])).role || null; }
  catch { return null; }
}

export function AuthProvider({ children }) {
  const [token,   setToken]   = useState(() => localStorage.getItem("hostsync_token"));
  const [checked, setChecked] = useState(false);
  const [features, setFeatures] = useState({});
  // The signed-in person — name and photo for the header, kept here so a
  // change on the Profile page shows up in the header immediately.
  const [me, setMe] = useState(null);
  const role = token ? decodeRole(token) : null;

  async function refreshMe(t = token) {
    if (!t) { setMe(null); return; }
    try {
      const res = await fetch(`${API}/profile`, { headers: { Authorization: `Bearer ${t}` } });
      if (res.ok) setMe((await res.json()).data || null);
    } catch { /* the header just falls back to initials */ }
  }

  // Verify stored token on app load
  useEffect(() => {
    async function verify() {
      if (!token) { setChecked(true); return; }
      try {
        const res = await fetch(`${API}/auth/verify`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (!json.valid) { localStorage.removeItem("hostsync_token"); setToken(null); }
        else { setFeatures(json.features || {}); refreshMe(token); }
      } catch {
        // If server is down, keep the token and let individual requests fail
      } finally {
        setChecked(true);
      }
    }
    verify();
  }, []);

  function login(t, f) {
    localStorage.setItem("hostsync_token", t);
    setToken(t);
    setFeatures(f || {});
    refreshMe(t);
  }

  function logout() {
    localStorage.removeItem("hostsync_token");
    setToken(null);
    setMe(null);
  }

  // Convenience: return fetch options with auth header
  function authHeaders() {
    return { Authorization: `Bearer ${token}` };
  }

  return (
    <AuthContext.Provider value={{ token, role, features, login, logout, authHeaders, checked, me, setMe, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
